import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BlockStack, Card, FormLayout, Layout, Page, Select, Text, TextField } from '@shopify/polaris';
import { useAxiosInstance } from '../../context/AxiosContext';
import { useCreatePromotion, useUpdatePromotion, CARDS } from './api';
import { useEditorForm } from './useEditorForm';
import { useReferrers } from './Layout';
import AppPicker from './AppPicker';
import ImageField from './ImageField';
import CardPreview from './CardPreview';
import StatusBadge from './StatusBadge';
import UnsavedChanges from './UnsavedChanges';

const BLANK = {
  app_name: '', description: '', app_url: '', offer_url: '', offer_code: '',
  special_offer_text: '', cta_text: '', app_icon: '', referrer_id: '',
  state: 'unpublished',
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:MM"; the API returns ISO8601 with a zone.

const STATES = [
  { label: 'Published', value: 'published' },
  { label: 'Unpublished', value: 'unpublished' },
  { label: 'Archived', value: 'archived' },
];

const CardEditor = ({ card, onClose }) => {
  const axiosInstance = useAxiosInstance();
  const referrers = useReferrers();
  const isNew = !card.id;

  const referrerOptions = referrers.map(({ id, label }) => ({ label, value: String(id) }));

  const create = useCreatePromotion({ resource: CARDS, modelKey: 'partner_card', axiosInstance });
  const update = useUpdatePromotion({ resource: CARDS, modelKey: 'partner_card', axiosInstance });
  const mutation = isNew ? create : update;

  const {
    confirmingExit, dirty, discard, errorFor,
    keepEditing, leaveAnyway, requestClose, setField, submit, values,
  } = useEditorForm({
    record: card,
    blank: BLANK,
    mutation,
    onClose,
    savedMessage: isNew ? 'Partner card created' : 'Partner card saved',
  });

  const objectUrl = useMemo(
    () => (values.image ? window.URL.createObjectURL(values.image) : null),
    [values.image],
  );
  useEffect(() => () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); }, [objectUrl]);

  const preview = { ...values, previewImage: objectUrl || values.app_icon || values.logo_url };

  return (
    <Page
      fullWidth
      backAction={{ content: 'Partner cards', onAction: requestClose }}
      title={isNew ? 'New partner card' : (values.app_name || 'Partner card')}
      titleMetadata={card.status ? <StatusBadge status={card.status} /> : undefined}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">

            <Card>
              <BlockStack gap="200">
                <Select
                  label="Status"
                  options={STATES}
                  value={values.state}
                  onChange={setField('state')}
                  error={errorFor('state')}
                />
                <Text as="span" variant="bodySm" tone="subdued">
                  Only a published card appears on the grid, and only in the apps picked below.
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingSm">Card content</Text>

                <ImageField
                  label="App icon"
                  required
                  shape="icon"
                  url={values.app_icon}
                  file={values.image}
                  onUrlChange={setField('app_icon')}
                  onFileChange={setField('image')}
                  error={errorFor('app_icon')}
                  hint="Square, 240 × 240 or larger. Cropped to a 60 × 60 circle on the merchant page, so keep the logo centred."
                />

                <FormLayout>
                  <Select
                    label="Partner"
                    requiredIndicator
                    placeholder="Select a partner"
                    options={referrerOptions}
                    value={String(values.referrer_id || '')}
                    onChange={setField('referrer_id')}
                    error={errorFor('referrer')}
                    helpText="The partner account this card belongs to."
                  />

                  <TextField
                    label="App name"
                    requiredIndicator
                    autoComplete="off"
                    value={values.app_name || ''}
                    onChange={setField('app_name')}
                    error={errorFor('app_name')}
                  />

                  <TextField
                    label="Short description"
                    requiredIndicator
                    multiline={2}
                    autoComplete="off"
                    value={values.description || ''}
                    onChange={setField('description')}
                    error={errorFor('description')}
                  />

                  <FormLayout.Group>
                    <TextField
                      label="Button text"
                      autoComplete="off"
                      placeholder="Add app"
                      value={values.cta_text || ''}
                      onChange={setField('cta_text')}
                    />
                    <TextField
                      label="Offer text"
                      autoComplete="off"
                      placeholder="20% OFF"
                      value={values.special_offer_text || ''}
                      onChange={setField('special_offer_text')}
                    />
                  </FormLayout.Group>

                  <TextField
                    label="Discount code"
                    autoComplete="off"
                    helpText="Stored for the record. Merchants do not see it - the offer text above is what appears."
                    value={values.offer_code || ''}
                    onChange={setField('offer_code')}
                  />

                  <FormLayout.Group>
                    <TextField
                      label="App link"
                      requiredIndicator
                      autoComplete="off"
                      helpText="App Store listing. Merchants do not click it - it is what the rating and review count are read from."
                      value={values.app_url || ''}
                      onChange={setField('app_url')}
                      error={errorFor('app_url')}
                    />
                    <TextField
                      label="Offer link"
                      requiredIndicator
                      autoComplete="off"
                      helpText="Where the whole card goes: icon, name and button"
                      value={values.offer_url || ''}
                      onChange={setField('offer_url')}
                      error={errorFor('offer_url')}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <AppPicker
                  codes={values.app_codes}
                  overrides={values.overrides}
                  onChange={setField('app_codes')}
                  onOverridesChange={setField('overrides')}
                  error={errorFor('app_dashboard')}
                />
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <div style={{ position: 'sticky', top: 16 }}>
            <Card>
              <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Merchant preview</Text>
                <CardPreview values={preview} />
              </BlockStack>
            </Card>
          </div>
        </Layout.Section>
      </Layout>

      <UnsavedChanges
        confirmingExit={confirmingExit}
        dirty={dirty}
        saving={mutation.isLoading}
        onSave={submit}
        onDiscard={discard}
        onKeep={keepEditing}
        onLeave={leaveAnyway}
      />

    </Page>
  );
};

CardEditor.propTypes = {
  card: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CardEditor;
